<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  useZone,
  useGridPrices,
  usePostHeight,
  copenhagenHour,
  type PriceZone,
} from '@greenbow/composables'
import SavingsCalculator, {
  type OptimalSlot,
  type CalculatorStateSnapshot,
} from './components/SavingsCalculator.vue'
import PriceChart from './components/PriceChart.vue'
import ErrorState from './components/ErrorState.vue'
import WidgetShareUrl from './components/WidgetShareUrl.vue'
import { readUrlState, buildShareUrl } from './composables/useUrlState'
import { DEFAULT_CHARGE_RATE_KW } from './lib/calculateSavings'

// In dev we hit the Vite proxy at /api (avoids CORS against staging).
// In prod we use VITE_API_BASE_URL or fall back to the staging URL.
const apiBaseUrl =
  import.meta.env.VITE_API_BASE_URL ??
  (import.meta.env.DEV ? '/api' : 'https://api.staging.greenbow.dk')

// Read the URL once on mount. After that, user interactions become the
// source of truth — we never write back to window.location.
const initialUrlState = readUrlState()

const { zone, setZone } = useZone({
  widget: 'smartcharge',
  default: initialUrlState.zone ?? 'DK2',
  urlParam: 'zone',
})

const { today, tomorrow, error, refresh } = useGridPrices({
  apiBaseUrl,
  zone,
})

usePostHeight()

onMounted(() => {
  void refresh()
})

const currentHour = computed(() => copenhagenHour())

const optimalSlots = ref<OptimalSlot[]>([])

// Mirror of the SavingsCalculator state so we can build the share URL.
const calculatorState = ref<CalculatorStateSnapshot>({
  chargeRate: initialUrlState.rate ?? DEFAULT_CHARGE_RATE_KW,
  kwh: initialUrlState.kwh ?? 50,
  kmPerYear: initialUrlState.km ?? 15000,
  deadline: initialUrlState.deadline ?? '07:00',
  showAnnual: initialUrlState.annual ?? false,
})

const chartOpen = ref(initialUrlState.chart ?? false)

function onZoneChange(next: PriceZone) {
  setZone(next)
}

function onOptimalSlotsChange(slots: OptimalSlot[]) {
  optimalSlots.value = slots
}

function onCalculatorStateChange(state: CalculatorStateSnapshot) {
  calculatorState.value = state
}

function onChartToggle(open: boolean) {
  chartOpen.value = open
}

const shareBaseUrl = computed(() => {
  if (typeof window === 'undefined') {
    return 'https://smartcharge.widget.greenbow.dk/v1/'
  }
  return `${window.location.origin}${window.location.pathname}`
})

const shareUrl = computed(() =>
  buildShareUrl(shareBaseUrl.value, {
    zone: zone.value,
    rate: calculatorState.value.chargeRate,
    kwh: calculatorState.value.kwh,
    km: calculatorState.value.kmPerYear,
    deadline: calculatorState.value.deadline,
    annual: calculatorState.value.showAnnual,
    chart: chartOpen.value,
  }),
)
</script>

<template>
  <main class="smartcharge">
    <ErrorState v-if="error" @retry="refresh()" />

    <template v-else>
      <SavingsCalculator
        :today="today"
        :tomorrow="tomorrow"
        :current-hour="currentHour"
        :zone="zone"
        :initial-charge-rate="calculatorState.chargeRate"
        :initial-kwh="calculatorState.kwh"
        :initial-km-per-year="calculatorState.kmPerYear"
        :initial-deadline="calculatorState.deadline"
        :initial-show-annual="calculatorState.showAnnual"
        @zone-change="onZoneChange"
        @optimal-slots-change="onOptimalSlotsChange"
        @state-change="onCalculatorStateChange"
      />

      <PriceChart
        :today="today"
        :tomorrow="tomorrow"
        :current-hour="currentHour"
        :optimal-slots="optimalSlots"
        :default-open="chartOpen"
        @toggle="onChartToggle"
      />

      <WidgetShareUrl :url="shareUrl" />
    </template>

    <footer class="smartcharge__footer">
      <a class="smartcharge__footer-link" href="https://greenbow.dk" target="_blank" rel="noopener">
        Bygget af <strong>greenbow.dk</strong>
      </a>
    </footer>
  </main>
</template>

<style lang="scss" scoped>
.smartcharge {
  display: flex;
  flex-direction: column;
  gap: space(5);

  &__footnote {
    margin: space(2) 0 0;
    font-size: $font-size-xs;
    color: color('ink-light');
    font-style: italic;
  }

  &__footer {
    display: flex;
    justify-content: center;
    padding-top: space(4);
    border-top: 1px solid color('background-medium');
  }

  &__footer-link {
    color: color('ink-light');
    font-size: $font-size-xs;
    text-decoration: none;
    transition: color $duration-base $easing-standard;

    strong {
      color: color('accent-dark');
      font-weight: $font-weight-semibold;
    }

    &:hover {
      color: color('ink');
    }

    &:focus-visible {
      outline: 2px solid color('accent');
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
